import {field, json, lazy, text} from '@nozbe/watermelondb/decorators';
import {Base} from 'src/models/base';
import {sanitizeStrings} from 'src/common-utils/sanitize-strings';
import {Q} from '@nozbe/watermelondb';

export class Instance extends Base {
  static table = 'instances';

  @text('name') name;
  @text('dba') dba;
  @text('logo') logo;
  @json('address', value => value) address;
  @json('billing_address', value => value) billingAddress;
  @field('reporting') reporting;
  @text('tin') tin;
  @text('npi') npi;
  @text('note_template') noteTemplate;
  @text('account_number') accountNumber;
  @field('billing') billing;
  @text('diagnoses') diagnoses;
  @field('single_address') singleAddress;
  @field('actual_time') actualTime;
  @field('use_client_identifier') useClientIdentifier;
  @field('show_ai_session_narrative') showAISessionNarrative;
  @json('taxonomy', sanitizeStrings) taxonomy;

  @lazy
  notesDue = (partition: string) =>
    this.collections
      .get('sessions')
      .query(
        Q.where('start_timestamp', Q.notEq(null)),
        Q.where('end_timestamp', Q.notEq(null)),
        Q.where('submission_timestamp', Q.eq(null)),
        Q.where('deleted_at', null),
        Q.where('_partition', partition),
      );

  @lazy
  sessionsCompleted = (partition: string) =>
    this.collections
      .get('sessions')
      .query(
        Q.where('start_timestamp', Q.notEq(null)),
        Q.where('end_timestamp', Q.notEq(null)),
        Q.where('submission_timestamp', Q.notEq(null)),
        Q.where('deleted_at', null),
        Q.where('_partition', partition),
      );

  @lazy
  totalSessions = (partition: string) =>
    this.collections
      .get('sessions')
      .query(Q.where('deleted_at', null), Q.where('_partition', partition));

  @lazy
  activePatients = (partition: string) => {
    const since = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return this.collections
      .get('patients')
      .query(
        Q.where('deleted_at', null),
        Q.where('state', Q.notEq('archived')),
        Q.where('_partition', partition),
        Q.on('sessions', [
          Q.where('deleted_at', null),
          Q.where('end_timestamp', Q.notEq(null)),
          Q.where('end_timestamp', Q.gte(since)),
        ]),
      );
  };

  @lazy
  inactivePatients = (partition: string) =>
    this.collections
      .get('patients')
      .query(
        Q.where('deleted_at', Q.notEq(null)),
        Q.where('_partition', partition),
      );

  @lazy
  totalPatients = (partition: string) =>
    this.collections.get('patients').query(Q.where('_partition', partition));

  @lazy
  activeStaff = (partition: string) =>
    this.collections
      .get('users')
      .query(
        Q.where('deleted_at', null),
        Q.where('state', Q.notEq('archived')),
        Q.where('_partition', partition),
      );

  @lazy
  inactiveStaff = (partition: string) =>
    this.collections
      .get('users')
      .query(
        Q.where('deleted_at', Q.notEq(null)),
        Q.where('_partition', partition),
      );

  @lazy
  totalStaff = (partition: string) =>
    this.collections.get('users').query(Q.where('_partition', partition));
}
